/**
 * Eagerly evaluates every `requires permission:` clause on a tour
 * against an authorize callback and returns a synchronous lookup the
 * runner consumes via its `checkPermission` option.
 *
 * The runner can call the resulting callback during gating evaluation
 * AND from the `can()` expression builtin without further IO. Lookups
 * for permissions that weren't prefetched return false. Failures fail
 * closed — a missing/unreachable PDP must not silently grant access.
 */

import type { Tour } from '@empowernow/etl-tour';

function splitPermission(input: string): { resourceType: string; action: string } {
  const lastDot = input.lastIndexOf('.');
  if (lastDot <= 0 || lastDot >= input.length - 1) {
    return { resourceType: '*', action: input };
  }
  return {
    resourceType: input.slice(0, lastDot),
    action: input.slice(lastDot + 1),
  };
}

export async function prefetchTourPermissions(
  authorize: (resourceType: string, action: string) => Promise<boolean>,
  tour: Tour,
): Promise<(action: string, resource?: string) => boolean> {
  const cache = new Map<string, boolean>();
  const reqs = tour.requires?.filter((r) => r.kind === 'permission') ?? [];
  await Promise.all(
    reqs.map(async (r) => {
      const { resourceType, action } = splitPermission(r.value);
      try {
        const allowed = await authorize(resourceType, action);
        cache.set(r.value, allowed);
      } catch {
        cache.set(r.value, false);
      }
    }),
  );
  return (action: string, resource?: string): boolean => {
    void resource;
    return cache.get(action) ?? false;
  };
}
